import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Footer } from './LandingPage';
import { agrogestao } from '@/api/agrogestaoClient';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

const PageHeader = ({ title, subtitle }) => (
  <div className="relative bg-green-900/20 py-20 sm:py-32">
    <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-4xl md:text-6xl font-bold tracking-tight text-white"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-green-100/80"
      >
        {subtitle}
      </motion.p>
    </div>
  </div>
);

const ContactSection = () => {
  const contactInfo = [
    {
      icon: <Mail className="w-7 h-7 text-amber-400" />,
      title: "E-mail",
      content: "hugo@aguiatecnologia.com.br",
      href: "mailto:hugo@aguiatecnologia.com.br"
    },
    {
      icon: <Phone className="w-7 h-7 text-amber-400" />,
      title: "Telefone",
      content: "(64) 99264 8848",
      href: "tel:+5564992648848"
    },
    {
      icon: <MapPin className="w-7 h-7 text-amber-400" />,
      title: "Endereço",
      content: "Rio Verde, GO - Brasil",
      href: "#"
    }
  ];

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', text: '' });
    const name = String(form.name || '').trim();
    const email = String(form.email || '').trim();
    const subject = String(form.subject || '').trim();
    const message = String(form.message || '').trim();
    if (!name || !email || !subject || !message) {
      setFeedback({ type: 'error', text: 'Preencha todos os campos.' });
      return;
    }
    setSending(true);
    const content = `Mensagem de contato\n\nNome: ${name}\nE-mail: ${email}\nAssunto: ${subject}\n\n${message}`;
    const res = await agrogestao.integrations.Core.SendEmail({ to: 'hugo@aguiatecnologia.com.br', subject: `Contato: ${subject}`, content });
    if (res && res.success) {
      setFeedback({ type: 'success', text: 'Mensagem enviada com sucesso.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } else {
      setFeedback({ type: 'error', text: 'Erro ao enviar. Tente novamente.' });
    }
    setSending(false);
  };

  return (
    <div className="py-20 sm:py-24 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Formulário de Contato */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="bg-green-900/20 border border-green-800/30 rounded-lg p-8"
          >
            <h2 className="text-3xl font-bold text-white mb-6">Envie sua mensagem</h2>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-green-100/90 mb-2">Nome</label>
                <Input id="name" name="name" type="text" placeholder="Seu nome completo" className="bg-gray-900/50 border-green-700/50 text-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-green-100/90 mb-2">E-mail</label>
                <Input id="email" name="email" type="email" placeholder="seu@email.com" className="bg-gray-900/50 border-green-700/50 text-white" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-green-100/90 mb-2">Assunto</label>
                <Input id="subject" name="subject" type="text" placeholder="Sobre o que você quer falar?" className="bg-gray-900/50 border-green-700/50 text-white" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-green-100/90 mb-2">Mensagem</label>
                <Textarea id="message" name="message" rows={5} placeholder="Digite sua mensagem aqui..." className="bg-gray-900/50 border-green-700/50 text-white" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              {feedback.text && (
                <Alert variant={feedback.type === 'error' ? 'destructive' : 'default'}>
                  <AlertTitle>{feedback.type === 'error' ? 'Erro' : 'Sucesso'}</AlertTitle>
                  <AlertDescription>{feedback.text}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" size="lg" className="w-full bg-amber-500 hover:bg-amber-600 text-green-950 font-bold group" disabled={sending}>
                Enviar Mensagem
                <Send className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
              </Button>
            </form>
          </motion.div>

          {/* Informações de Contato */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-8 pt-8"
          >
            <h2 className="text-3xl font-bold text-white mb-6">Outras formas de contato</h2>
            {contactInfo.map((info, index) => (
              <a key={index} href={info.href} className="flex items-start gap-6 group">
                <div className="flex-shrink-0 bg-green-900/30 p-4 rounded-lg border border-green-800/50 group-hover:border-amber-400/50 transition-colors">
                  {info.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors">{info.title}</h3>
                  <p className="text-lg text-green-100/80">{info.content}</p>
                </div>
              </a>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default function Contato() {
  return (
    <div className="bg-gray-950">
      <PageHeader 
        title="Fale Conosco"
        subtitle="Estamos aqui para ajudar. Envie suas dúvidas, sugestões ou solicite uma demonstração personalizada."
      />
      <ContactSection />
      <Footer />
    </div>
  );
}
